import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { convertFromDn, convertFromInch, convertFromMm } from "../lib/conversions";
import { useNavigate } from "react-router-dom";
import { Search, ArrowRight } from "lucide-react";

const MODES_NL = [
  { key: "mm", label: "D1 (mm)", placeholder: "Bijv. 33.7" },
  { key: "dn", label: "DN", placeholder: "Bijv. 25" },
  { key: "inch", label: "Inch", placeholder: 'Bijv. 1"' },
];

const MODES_EN = [
  { key: "mm", label: "D1 (mm)", placeholder: "e.g. 33.7" },
  { key: "dn", label: "DN", placeholder: "e.g. 25" },
  { key: "inch", label: "Inch", placeholder: 'e.g. 1"' },
];

export default function PipeClampFinder({ compact = false, lang: forcedLang }) {
  const { i18n, t } = useTranslation();
  const activeLang = (forcedLang || i18n.language || "nl").split("-")[0];
  // English placeholders only when language is explicitly English; other
  // non-NL languages use i18n-translated strings via t().
  const MODES = activeLang === "en"
    ? MODES_EN
    : activeLang === "nl"
      ? MODES_NL
      : [
          { key: "mm", label: "D1 (mm)", placeholder: t("finder.placeholder_mm") },
          { key: "dn", label: t("finder.mode_dn"), placeholder: t("finder.placeholder_dn") },
          { key: "inch", label: t("finder.mode_inch"), placeholder: t("finder.placeholder_inch") },
        ];
  const txt = {
    title: t("finder.title"),
    search: t("finder.search"),
    findMyClamp: activeLang === "en" ? "Find my clamp" : t("finder.find_my_clamp"),
    searchPlaceholder: t("finder.search_dn_inch_mm"),
  };
  const [mode, setMode] = useState("mm");
  const [value, setValue] = useState("");
  const navigate = useNavigate();

  const conversion = useMemo(() => {
    if (!value) return null;
    if (mode === "mm") return convertFromMm(parseFloat(value));
    if (mode === "dn") return convertFromDn(value);
    if (mode === "inch") return convertFromInch(value);
    return null;
  }, [value, mode]);

  const handleSearch = () => {
    if (!conversion) return;
    // Send ONLY the unit the user actually entered to the catalog. Avoids
    // collisions when the conversion table says (DN25 → 28mm) but the user
    // explicitly searched DN25 — the detail page should match on DN, not
    // be hijacked by a derived diameter (which would resolve to a
    // different DN per Nibro's mapping).
    const params = new URLSearchParams();
    if (mode === "mm") params.set("d1_mm", conversion.d1_mm);
    else if (mode === "dn") params.set("dn", conversion.dn);
    else if (conversion.inch) params.set("inch", conversion.inch);
    navigate(`/products?${params.toString()}`);
  };

  if (compact) {
    // Compact variant for the trust strip
    return (
      <div className="w-full rounded-md border-2 border-nibro-sage bg-white p-2" data-testid="pipe-clamp-finder-compact">
        <div className="flex items-center gap-2">
          <Search size={16} className="text-nibro-sage-dark" />
          <input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={txt.searchPlaceholder}
            className="flex-1 bg-transparent text-sm placeholder:text-zinc-400 focus:outline-none"
            data-testid="finder-input-compact"
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
          <button
            type="button"
            onClick={handleSearch}
            disabled={!conversion}
            className="rounded bg-nibro-sage px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-nibro-sage-hover disabled:opacity-50"
            data-testid="finder-search-button-compact"
          >
            {txt.search}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-lg border-2 border-nibro-sage bg-white p-6 shadow-lg md:p-8"
      data-testid="pipe-clamp-finder"
    >
      <div className="mb-5 flex items-center gap-2">
        <Search size={16} className="text-nibro-sage-dark" />
        <span className="label-uppercase">Pipe Clamp Finder</span>
      </div>

      <div
        className="mb-5 inline-flex rounded-md border border-zinc-200 bg-zinc-50 p-1"
        data-testid="finder-mode-tabs"
      >
        {MODES.map((m) => (
          <button
            type="button"
            key={m.key}
            onClick={() => { setMode(m.key); setValue(""); }}
            className={`rounded px-4 py-2 text-xs font-bold uppercase tracking-wider transition ${
              mode === m.key
                ? "bg-nibro-sage text-white shadow-sm"
                : "text-zinc-500 hover:text-zinc-900"
            }`}
            data-testid={`finder-mode-${m.key}`}
          >
            {m.label}
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row">
        <input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={MODES.find((m) => m.key === mode).placeholder}
          className="flex-1 rounded-md border border-zinc-200 bg-white px-4 py-4 text-lg font-semibold text-zinc-900 placeholder:text-zinc-400 focus:border-nibro-sage focus:outline-none focus:ring-2 focus:ring-nibro-sage/30"
          data-testid="finder-input"
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
        />
        <button
          type="button"
          onClick={handleSearch}
          disabled={!conversion}
          className="flex items-center justify-center gap-2 rounded-md bg-nibro-sage px-6 py-4 text-sm font-bold text-white transition hover:bg-nibro-sage-hover disabled:opacity-50"
          data-testid="finder-search-button"
        >
          {txt.findMyClamp} <ArrowRight size={16} />
        </button>
      </div>

      {conversion && (
        <div
          className="mt-6 grid grid-cols-3 gap-3 rounded-md bg-nibro-sage-pale p-4"
          data-testid="finder-conversion-preview"
        >
          <ConvCell label="D1" value={`${conversion.d1_mm} mm`} />
          <ConvCell label="DN" value={conversion.dn ? `DN${conversion.dn}` : "—"} />
          <ConvCell label="Inch" value={conversion.inch || "—"} />
        </div>
      )}
    </div>
  );
}

function ConvCell({ label, value }) {
  return (
    <div className="flex flex-col items-center justify-center rounded bg-white p-3">
      <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-nibro-sage-dark">{label}</div>
      <div className="mt-1 font-display text-xl font-bold text-zinc-900">{value}</div>
    </div>
  );
}
